import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  type SetStateAction,
} from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'

import { RootState } from '../../store'
import { InvestV2Presenter } from './invest-v2-presenter'
import {
  projectServices,
  kycServices,
  investServices,
  documentServices,
  walletServices,
} from '../../services'
import { WalletSummary, WalletTopup } from '../../services/wallet-service'
import {
  InterestIncomePreference,
  Investment,
  OpportunityDetails,
  UpdateInvestmentPayload,
  UserInvestmentDetails,
} from '../../lib/types'
import { showBanner } from '../../global-state/banner-slice'
import { captureEvent } from '../../lib/utils/posthogUtils/usePostHog'
import { EVENTS } from '../../lib/utils/constants'
import {
  isTransferPageEnabled,
  showInterestIncomePreferenceOption,
  parseInterestIncomePreference,
  getRemainingCanInvestAmountUsd,
  sanitizeMetadataForUpdate,
} from './invest-v2-utils'
import { investmentNeedsWalletPayment } from '../dashboard-v2/dashboard-v2.utils'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
import {
  parseTermsheetAgreementPollResponse,
  isTermsheetSigningDone,
  TERMSHEET_POLL_INTERVAL_MS,
} from '../../lib/utils/termsheet-agreement'
import './invest-v2.scss'
import { getSharePricing, type ShareCalculatorChange } from './invest-v2-share-order'
import { PLATFORM_FEE, PLATFORM_FEE_CURRENCY } from '../payment/constants'
import { storeOpportunityComplianceReturn } from '../../lib/utils/compliance-return-redirect'
import ExtendLimitModal from '../../lib/components/ExtendLimitModal/extend-limit-modal'
import { usePendingInvestmentLimitRequest } from '../../lib/utils/use-pending-investment-limit-request'

type OpportunityInvestmentPatchPayload = Parameters<
  typeof investServices.updateOpportunityInvestment
>[0]

const InvestV2Container = () => {
  const navigate = useNavigate()
  const params = useParams()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const { projectId: opportunityParamId, projectSlug: opportunitySlug } = params
  const accessToken = searchParams.get('access')
  const { authToken, userId } = useSelector((state: RootState) => state.investor)
  const [opportunity, setOpportunity] = useState<OpportunityDetails>()
  const [userInvestmentDetail, setUserInvestmentDetail] = useState<UserInvestmentDetails>()
  const [investmentData, setInvestmentData] = useState<Investment>()
  const [wizardStep, setWizardStep] = useState(1)
  const wizardHydratedRef = useRef(false)
  const [investmentValue, setInvestmentValue] = useState<number>(0)
  // Share-priced deals only: whole shares and whether the calculator's order is valid.
  const [investmentShares, setInvestmentShares] = useState<number>(0)
  const [isShareOrderValid, setIsShareOrderValid] = useState<boolean>(false)
  const sharePricing = useMemo(() => getSharePricing(opportunity), [opportunity])
  const [investmentValueForTermsheet, setInvestmentValueForTermsheet] = useState<number>(0)
  const [interestIncomePreference, setInterestIncomePreference] =
    useState<InterestIncomePreference>('payout')
  const [isApiError, setIsApiError] = useState<boolean>(false)
  const [showTransactionChecklistModal, setTransactionChecklistModal] = useState(false)
  const [isTransactionChecklistChecked, setIsTransactionChecklistChecked] = useState(false)
  const [isTermsheetAgreementChecked, setIsTermsheetAgreementChecked] = useState(false)
  const [isTermsheetAgreementNeeded, setIsTermsheetAgreementNeeded] = useState(false)
  const [acceptTOBCheckbox, setAcceptTOBCheckbox] = useState(false)
  const [showPopupBlockedModal, setShowPopupBlockedModal] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  /** Risk / term sheet ack — do not use full-page loader (avoids layout jump on check) */
  const [isInlineAckLoading, setIsInlineAckLoading] = useState(false)
  const [investmentEssentialsAck, setInvestmentEssentialsAck] = useState(false)
  const userHasEditedRiskAckRef = useRef(false)
  const userHasEditedTobRef = useRef(false)
  const termsheetPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [isTermsheetSigningPending, setIsTermsheetSigningPending] = useState(false)
  const [termsheetSigningLink, setTermsheetSigningLink] = useState<string | null>(null)
  const [termsheetDownloadLink, setTermsheetDownloadLink] = useState<string | null>(null)
  const [termsheetRequiresResign, setTermsheetRequiresResign] = useState(false)
  const [termsheetResignMessage, setTermsheetResignMessage] = useState<string | undefined>()
  const [wallet, setWallet] = useState<WalletSummary>()
  const [pendingTopup, setPendingTopup] = useState<WalletTopup>()
  const [showExtendLimitModal, setShowExtendLimitModal] = useState(false)
  const { hasPendingRequest, markPending, openIfAllowed } = usePendingInvestmentLimitRequest()

  useEffect(() => {
    if (accessToken && opportunitySlug && !authToken) {
      navigate('/opportunities')
    }
  }, [accessToken, opportunitySlug, authToken, navigate])

  const stopTermsheetPolling = useCallback(() => {
    if (termsheetPollRef.current) {
      clearInterval(termsheetPollRef.current)
      termsheetPollRef.current = null
    }
    setIsTermsheetSigningPending(false)
  }, [])

  const applyTermsheetStateFromPoll = useCallback(
    (result: ReturnType<typeof parseTermsheetAgreementPollResponse>) => {
      setTermsheetRequiresResign(result.requiresResign)
      setTermsheetResignMessage(result.resignMessage)

      if (result.canContinue) {
        setIsTermsheetAgreementChecked(true)
        if (result.downloadLink) {
          setTermsheetDownloadLink(result.downloadLink)
        }
        return
      }

      setIsTermsheetAgreementChecked(false)
      setTermsheetDownloadLink(null)
    },
    [],
  )

  const pollTermsheetStatus = useCallback(async () => {
    if (!opportunity?.id || !investmentData?.id) return false

    const res = await investServices.getSignedTermsheetAgreementDocument(
      String(opportunity.id),
      String(investmentData.id),
      { Authorization: authToken ?? '' },
    )
    const result = parseTermsheetAgreementPollResponse(res)

    // Close signing iframe immediately when signed/COMPLETED — do not wait for ready/redirect
    if (isTermsheetSigningDone(result)) {
      setTermsheetSigningLink(null)
    }

    applyTermsheetStateFromPoll(result)

    if (!result.canContinue) return false

    stopTermsheetPolling()
    logCrmEvent(
      EngagementEvents.INVESTOR_ACCEPTED_TERMSHEET_AGREEMENT,
      { Authorization: authToken ?? '' },
      {
        opportunityName: opportunity?.projectName,
        opportunityId: opportunity?.id,
      },
    )
    return true
  }, [
    applyTermsheetStateFromPoll,
    authToken,
    investmentData?.id,
    opportunity?.id,
    opportunity?.projectName,
    stopTermsheetPolling,
  ])

  const syncExistingTermsheetState = useCallback(
    async (investmentIdOverride?: number) => {
      const investmentId = investmentIdOverride ?? investmentData?.id
      if (!isTermsheetAgreementNeeded || !opportunity?.id || !investmentId) return

      const res = await investServices.getSignedTermsheetAgreementDocument(
        String(opportunity.id),
        String(investmentId),
        { Authorization: authToken ?? '' },
      )
      applyTermsheetStateFromPoll(parseTermsheetAgreementPollResponse(res))
    },
    [
      applyTermsheetStateFromPoll,
      authToken,
      investmentData?.id,
      isTermsheetAgreementNeeded,
      opportunity?.id,
    ],
  )

  const applyWizardFromCompletedSteps = useCallback((cs: number) => {
    if (cs >= 2) setWizardStep(3)
    else if (cs >= 1) setWizardStep(2)
    else setWizardStep(1)
  }, [])

  const hydrateInvestmentIntoWizard = (investment: Investment, resumeWalletPayment: boolean) => {
    setInvestmentData(investment)
    const displayAmount = Number(investment?.amount) / 100
    setInvestmentValue(displayAmount)
    setInvestmentValueForTermsheet(displayAmount)
    setInterestIncomePreference(parseInterestIncomePreference(investment.metadata))
    if (typeof investment.shares === 'number') setInvestmentShares(investment.shares)

    if (!wizardHydratedRef.current) {
      if (resumeWalletPayment) {
        setWizardStep(3)
      } else if (investment.metadata) {
        applyWizardFromCompletedSteps(Number(investment.metadata.completedSteps))
      }
      wizardHydratedRef.current = true
    }

    if (isTermsheetAgreementNeeded && investment.id) {
      void syncExistingTermsheetState(investment.id)
    }
  }

  const getInvestment = async () => {
    const opportunityIdForApi = accessToken ? opportunity?.id?.toString() : opportunityParamId
    if (!opportunityIdForApi) return

    const initiated = await investServices.getInvestmentByOpportunityId(
      opportunityIdForApi,
      'INITIATED',
      {
        Authorization: authToken ? authToken : '',
      },
    )

    if (initiated && initiated.data && initiated.status === 200) {
      hydrateInvestmentIntoWizard(initiated.data, false)
      return
    }

    const paymentDone = await investServices.getInvestmentByOpportunityId(
      opportunityIdForApi,
      'PAYMENT_DONE',
      {
        Authorization: authToken ? authToken : '',
      },
    )

    // Wallet was debited but the order was not placed (e.g. second call failed): resume at the pay step.
    if (paymentDone && paymentDone.data && paymentDone.status === 200) {
      hydrateInvestmentIntoWizard(paymentDone.data, true)
      return
    }

    const orderPlaced = await investServices.getInvestmentByOpportunityId(
      opportunityIdForApi,
      'ORDER_PLACED',
      {
        Authorization: authToken ? authToken : '',
      },
    )

    if (orderPlaced && orderPlaced.data && orderPlaced.status === 200) {
      const needsPayment = investmentNeedsWalletPayment(
        orderPlaced.data.status,
        orderPlaced.data.metadata,
      )
      if (needsPayment) {
        hydrateInvestmentIntoWizard(orderPlaced.data, true)
        return
      }
      navigate(-1)
    }
  }

  const getUserInvestments = async () => {
    const response = await investServices.getUserInvestmentDetail_v1({
      Authorization: authToken ? authToken : '',
    })
    if (response && response.data && response.status === 200) {
      setUserInvestmentDetail(response.data)
    }
  }

  const fetchOpportunityById = async () => {
    let data
    if (accessToken && opportunitySlug) {
      if (!authToken) {
        navigate('/opportunities')
        return
      }
      data = await projectServices.getOpportunityBySlugAndToken(
        opportunitySlug as string,
        accessToken,
      )
    } else {
      data = await projectServices.getOpportunityById(opportunityParamId as string, {
        Authorization: authToken ? authToken : '',
      })
    }
    if (data && data.data && data.status === 200) {
      setOpportunity(data.data)
      const displayTicketSize = Number(data.data.ticketSize) / 100
      setInvestmentValue(displayTicketSize || 0)
      if (data.data.metadata?.termsheet_agreement_template_url) {
        setIsTermsheetAgreementNeeded(true)
      }
    }
  }

  const postInvestmentAmount = async (payload: {
    projectId: string
    investmentAmount: number
    shares?: number
    assetClass?: string
    metadata: {
      completedSteps: number
      interestIncomePreference?: InterestIncomePreference
    }
  }) => {
    setIsTransitioning(true)
    try {
      const response = await investServices.postOpportunityInvestment(payload, {
        Authorization: authToken ? authToken : '',
      })
      if (response.status === 200) {
        setIsApiError(false)
        setWizardStep(2)
        wizardHydratedRef.current = true
        await getInvestment()

        logCrmEvent(
          EngagementEvents.COMPLETED_FIRST_STEP_INVESTMENT,
          {
            Authorization: authToken ?? '',
          },
          {
            opportunityName: opportunity?.projectName,
            opportunityId: opportunity?.id,
          },
        )
      } else {
        const message = response.data.message || 'Error saving the investment'
        dispatch(showBanner({ text: message, variant: 'error-banner' }))
        setIsApiError(true)
      }
    } finally {
      setIsTransitioning(false)
    }
  }

  const applyInvestmentUpdateResponse = useCallback(
    (data: { data?: Investment; status?: number }, options?: { syncTermsheet?: boolean }) => {
      if (data?.data && data.status === 200) {
        setInvestmentData(data.data)
        const displayAmount = Number(data.data?.amount) / 100
        setInvestmentValue(displayAmount)
        setInvestmentValueForTermsheet(displayAmount)
        setInterestIncomePreference(parseInterestIncomePreference(data.data.metadata))
        if (typeof data.data.shares === 'number') setInvestmentShares(data.data.shares)
        setIsApiError(false)
        if (options?.syncTermsheet && isTermsheetAgreementNeeded) {
          void syncExistingTermsheetState(data.data.id)
        }
        return true
      }
      const message =
        (data as { data?: { message?: string } })?.data?.message || 'Error saving the investment'
      dispatch(showBanner({ text: message, variant: 'error-banner' }))
      setIsApiError(true)
      return false
    },
    [dispatch, isTermsheetAgreementNeeded, syncExistingTermsheetState],
  )

  const patchInvestmentAmount = async (
    payload: OpportunityInvestmentPatchPayload,
    options?: { syncTermsheet?: boolean },
  ) => {
    const data = await investServices.updateOpportunityInvestment(payload, {
      Authorization: authToken ? authToken : '',
    })
    return applyInvestmentUpdateResponse(data, options)
  }

  const patchInterestIncomePreference = async (
    payload: OpportunityInvestmentPatchPayload,
    options?: { syncTermsheet?: boolean },
  ) => {
    const data = await investServices.updateOpportunityInvestment(payload, {
      Authorization: authToken ? authToken : '',
    })
    return applyInvestmentUpdateResponse(data, options)
  }

  const handleShareOrderChange = useCallback((change: ShareCalculatorChange) => {
    setInvestmentShares(change.shares)
    setInvestmentValue(change.amountUsd)
    setIsShareOrderValid(change.isValid)
    setIsApiError(false)
  }, [])

  const shareOrderPayload = sharePricing ? { shares: investmentShares } : {}

  const saveInvestmentForTermsheetSigning = async (): Promise<
    { ok: true; amount: number } | { ok: false }
  > => {
    if (!investmentData?.id || !opportunity) return { ok: false }

    const opportunityIdNum = accessToken ? opportunity.id : Number(opportunityParamId)
    if (!opportunityIdNum) return { ok: false }

    const baseMetadata = {
      ...sanitizeMetadataForUpdate(investmentData.metadata),
      completedSteps: 1 as const,
    }

    const amountSaved = await patchInvestmentAmount({
      projectId: opportunityIdNum,
      userId: userId,
      investmentId: investmentData.id,
      investmentAmount: investmentValue.toString(),
      ...shareOrderPayload,
      metadata: baseMetadata,
    })
    if (!amountSaved) return { ok: false }

    if (!showInterestIncomePreferenceOption(opportunity)) {
      return { ok: true, amount: investmentValue }
    }

    const preferenceSaved = await patchInterestIncomePreference({
      projectId: opportunityIdNum,
      userId: userId,
      investmentId: investmentData.id,
      metadata: {
        ...baseMetadata,
        interestIncomePreference,
      },
    })
    return preferenceSaved ? { ok: true, amount: investmentValue } : { ok: false }
  }

  const handleInvestmentAmount = async () => {
    const opportunityIdStr = accessToken ? opportunity?.id?.toString() : opportunityParamId
    if (!opportunityIdStr) return

    const amountStepMetadata = {
      completedSteps: 1 as const,
      ...(showInterestIncomePreferenceOption((opportunity as OpportunityDetails) ?? {})
        ? { interestIncomePreference }
        : {}),
    }

    if (!investmentData) {
      const payload = {
        projectId: opportunityIdStr,
        investmentAmount: investmentValue,
        ...shareOrderPayload,
        assetClass: opportunity?.assetClass,
        metadata: amountStepMetadata,
      }
      await postInvestmentAmount(payload)
    } else if (investmentData.amount) {
      const opportunityIdNum = accessToken ? opportunity?.id : Number(opportunityParamId)
      if (!opportunityIdNum) return

      setIsTransitioning(true)
      try {
        const baseMetadata = {
          ...sanitizeMetadataForUpdate(investmentData.metadata),
          completedSteps: 1 as const,
        }

        const amountSaved = await patchInvestmentAmount(
          {
            projectId: opportunityIdNum,
            userId: userId,
            investmentId: investmentData.id,
            investmentAmount: investmentValue.toString(),
            ...shareOrderPayload,
            metadata: baseMetadata,
          },
          { syncTermsheet: false },
        )
        if (!amountSaved) return

        if (showInterestIncomePreferenceOption((opportunity as OpportunityDetails) ?? {})) {
          const preferenceSaved = await patchInterestIncomePreference(
            {
              projectId: opportunityIdNum,
              userId: userId,
              investmentId: investmentData.id,
              metadata: {
                ...baseMetadata,
                interestIncomePreference,
              },
            },
            { syncTermsheet: true },
          )
          if (!preferenceSaved) return
        } else if (isTermsheetAgreementNeeded) {
          void syncExistingTermsheetState(investmentData.id)
        }

        setWizardStep(2)
        wizardHydratedRef.current = true
      } finally {
        setIsTransitioning(false)
      }
    }
    await getUserInvestments()
  }

  const backToOpportunityHandler = () => {
    if (accessToken && opportunitySlug) {
      navigate(`/opportunity/link/${opportunitySlug}?access=${accessToken}`)
    } else if (opportunitySlug && opportunityParamId) {
      navigate(`/opportunity/${opportunitySlug}/${opportunityParamId}`)
    } else {
      navigate('/opportunities')
    }
  }

  const handleCancelFromAmountStep = async () => {
    if (!investmentData?.id) {
      backToOpportunityHandler()
      return
    }
    setIsTransitioning(true)
    try {
      const payload: UpdateInvestmentPayload = {
        investmentId: investmentData.id,
        userId: investmentData.investor_id,
        projectId: investmentData.opportunity_id,
        metadata: {
          ...sanitizeMetadataForUpdate(investmentData.metadata),
          completedSteps: 1,
          amountStepCancelled: true,
        },
      }
      const data = await investServices.updateOpportunityInvestment(payload, {
        Authorization: authToken ? authToken : '',
      })
      if (data && data.data && data.status === 200) {
        setInvestmentData(undefined)
        wizardHydratedRef.current = false
        const ticket = Number(opportunity?.ticketSize ?? 0) / 100
        setInvestmentValue(ticket || 0)
        setInvestmentValueForTermsheet(ticket || 0)
        setInterestIncomePreference('payout')
        setIsTermsheetAgreementChecked(false)
        setTermsheetDownloadLink(null)
        setTermsheetRequiresResign(false)
        setTermsheetResignMessage(undefined)
        stopTermsheetPolling()
        dispatch(
          showBanner({
            text: 'Investment cancelled',
            variant: 'success-banner',
          }),
        )
        await getUserInvestments()
        backToOpportunityHandler()
      } else {
        const message =
          (data as { data?: { message?: string } })?.data?.message ||
          'Could not cancel investment. Please try again.'
        dispatch(showBanner({ text: message, variant: 'error-banner' }))
        setIsApiError(true)
      }
    } finally {
      setIsTransitioning(false)
    }
  }

  const onWizardBack = () => {
    setIsApiError(false)
    if (wizardStep <= 1) return
    // Leaving mid-signing must not leave the "signing pending" flag on, or Continue stays
    // disabled on the previous step. A fresh signing link is minted when the sheet is reopened.
    stopTermsheetPolling()
    setTermsheetSigningLink(null)
    setWizardStep((s) => Math.max(1, s - 1))
  }

  const handleAcceptTermsheet = useCallback(async () => {
    if (!opportunity?.id || !investmentData?.id) return
    setIsInlineAckLoading(true)
    try {
      const saved = await saveInvestmentForTermsheetSigning()
      if (!saved.ok) return

      // Mint a fresh link on every open — SignNow links expire and are effectively single-use
      const response = await documentServices.createTermsheetSigningLink(
        opportunity.id.toString(),
        saved.amount.toString(),
        investmentData.id.toString(),
        { Authorization: authToken ? authToken : '' },
      )
      const signingLink =
        response?.data?.signingLink ??
        (response?.data as { data?: { signingLink?: string } } | undefined)?.data?.signingLink
      if (response?.status !== 200 || !signingLink) return

      setTermsheetSigningLink(signingLink)
      setIsTermsheetSigningPending(true)
    } finally {
      setIsInlineAckLoading(false)
    }
  }, [authToken, investmentData?.id, investmentValue, interestIncomePreference, opportunity?.id])

  const handleReopenTermsheetSigning = useCallback(() => {
    void handleAcceptTermsheet()
  }, [handleAcceptTermsheet])

  const handleTermsheetSigningComplete = useCallback(() => {
    setTermsheetSigningLink(null)
    void pollTermsheetStatus()
  }, [pollTermsheetStatus])

  // Poll while iframe is open, or after close until signed+ready so continue unlocks
  useEffect(() => {
    const iframeOpen = Boolean(termsheetSigningLink)
    const awaitingReady = isTermsheetSigningPending && !isTermsheetAgreementChecked
    if ((!iframeOpen && !awaitingReady) || !opportunity?.id || !investmentData?.id) return

    void pollTermsheetStatus()

    termsheetPollRef.current = setInterval(() => {
      void pollTermsheetStatus()
    }, TERMSHEET_POLL_INTERVAL_MS)

    return () => {
      if (termsheetPollRef.current) {
        clearInterval(termsheetPollRef.current)
        termsheetPollRef.current = null
      }
    }
  }, [
    investmentData?.id,
    isTermsheetAgreementChecked,
    isTermsheetSigningPending,
    opportunity?.id,
    pollTermsheetStatus,
    termsheetSigningLink,
  ])

  const handleDownloadSignedTermsheet = useCallback(() => {
    if (!termsheetDownloadLink) return
    window.open(termsheetDownloadLink)
  }, [termsheetDownloadLink])

  const handleAcknowledgeInvestmentEssentials = useCallback(async () => {
    userHasEditedRiskAckRef.current = true
    setIsInlineAckLoading(true)
    try {
      const data = await kycServices.acceptInvestmentEssentialAgreement(
        { declaration: true },
        { Authorization: authToken ? authToken : '' },
      )
      const statusOk =
        data &&
        typeof data === 'object' &&
        'status' in data &&
        (data as { status: number }).status === 200
      if (statusOk) {
        logCrmEvent(
          EngagementEvents.VIEWED_ACCEPTED_INVESTMENT_RISKS,
          { Authorization: authToken ?? '' },
          {
            opportunityName: opportunity?.projectName,
            opportunityId: opportunity?.id,
          },
        )
      }
      const kyc = await kycServices.getKYC_v1(userId.toString(), {
        Authorization: authToken ? authToken : '',
      })
      if (kyc?.data?.investment_essentials_agreement) {
        setInvestmentEssentialsAck(true)
      }
    } finally {
      setIsInlineAckLoading(false)
    }
  }, [authToken, opportunity?.projectName, opportunity?.id, userId])

  const isPrimaryDisabled = (): boolean => {
    const canInvestAmount = getRemainingCanInvestAmountUsd(
      userInvestmentDetail?.canInvest,
      investmentData?.amount,
    )
    if (isApiError) return true
    if (isTransitioning || isInlineAckLoading || isTermsheetSigningPending) return true
    if (wizardStep === 1) {
      if (sharePricing && !isShareOrderValid) return true
      if (
        !!opportunity?.ticketSize &&
        // Share-priced deals check the minimum on the final amount (see the calculator).
        (sharePricing || investmentValue >= parseInt(opportunity?.ticketSize, 10) / 100) &&
        investmentValue <=
          (opportunity?.raiseGoal
            ? Math.min(
                (parseInt(opportunity?.raiseGoal, 10) -
                  parseInt(opportunity?.committedAmount ?? '0', 10)) /
                  100,
                canInvestAmount,
              )
            : canInvestAmount)
      ) {
        return false
      }
      return true
    }

    if (wizardStep === 2) {
      return !(
        investmentEssentialsAck &&
        acceptTOBCheckbox &&
        (!isTermsheetAgreementNeeded || isTermsheetAgreementChecked)
      )
    }

    return true
  }

  const handleAgreementandBankDetailsConfirmation = async () => {
    setIsTransitioning(true)
    try {
      const payload = {
        investmentId: investmentData!.id,
        userId: investmentData!.investor_id,
        projectId: investmentData!.opportunity_id,
        assetClass: opportunity?.assetClass,
        metadata: {
          ...sanitizeMetadataForUpdate(investmentData!.metadata),
          completedSteps: 2,
          bankDetailsConfirmed: true,
        },
      }
      const data = await investServices.updateOpportunityInvestment(payload, {
        Authorization: authToken ? authToken : '',
      })
      if (data && data.data && data.status === 200) {
        setInvestmentData(data.data)
        const displayAmount = Number(data.data?.amount) / 100
        setInvestmentValue(displayAmount)
        dispatch(
          showBanner({
            text: 'Agreement submitted successfully',
            variant: 'success-banner',
          }),
        )
        setIsApiError(false)
        setWizardStep(3)
        wizardHydratedRef.current = true
      } else {
        dispatch(
          showBanner({
            text: (data.data && data.data.message) || 'Something went wrong! Please try again.',
            variant: 'error-banner',
          }),
        )
        setIsApiError(true)
      }
    } finally {
      setIsTransitioning(false)
    }
  }

  const refreshWallet = useCallback(async () => {
    const res = await walletServices.getWallet({ Authorization: authToken || '' })
    if (res?.status !== 200) return
    setWallet(res.data)
    const openFromApi = (res.data.openTopups || [])[0]
    const ledgerRef = (res.data.ledger || []).find(
      (row: { type: string; reference_id?: string }) =>
        row.type === 'TOPUP_PENDING' && row.reference_id !== undefined,
    )
    const openId = openFromApi?.id || Number(ledgerRef?.reference_id)
    if (!openId) {
      setPendingTopup(undefined)
      return
    }
    const topupRes = await walletServices.getTopup(openId, {
      Authorization: authToken || '',
    })
    if (topupRes?.status === 200) {
      const topup = topupRes.data as WalletTopup
      if (topup.status === 'INITIATED' || topup.status === 'ORDER_PLACED') {
        setPendingTopup(topup)
      } else {
        setPendingTopup(undefined)
      }
      return
    }
    setPendingTopup(openFromApi)
  }, [authToken])

  useEffect(() => {
    if (wizardStep === 3) void refreshWallet()
  }, [wizardStep, refreshWallet])

  // Server checks the balance and emails the investor once if it is short.
  const insufficientBalanceNotifiedRef = useRef<number>()
  useEffect(() => {
    const investmentId = investmentData?.id
    if (wizardStep !== 3 || !investmentId || !authToken) return
    if (insufficientBalanceNotifiedRef.current === investmentId) return
    insufficientBalanceNotifiedRef.current = investmentId
    void investServices.notifyInsufficientBalance(investmentId, { Authorization: authToken })
  }, [wizardStep, investmentData?.id, authToken])

  const handlePayFromWallet = async () => {
    if (!investmentData) return
    setIsTransitioning(true)
    try {
      const buildPayload = (
        status: 'PAYMENT_DONE' | 'ORDER_PLACED',
        metadata = investmentData.metadata,
      ) => ({
        userId: investmentData.investor_id,
        projectId: investmentData.opportunity_id,
        investmentId: investmentData.id,
        assetClass: opportunity?.assetClass,
        status,
        metadata: {
          ...sanitizeMetadataForUpdate(metadata),
          completedSteps: 3,
          payFromWallet: true,
        },
        platformFee: {
          amount: PLATFORM_FEE,
          currency: PLATFORM_FEE_CURRENCY,
        },
      })
      const headers = { Authorization: authToken || '' }

      // Step 1: pay from wallet (skipped when resuming an investment that is already paid).
      let paid = investmentData
      if (investmentData.status !== 'PAYMENT_DONE') {
        const paymentRes = await investServices.updateOpportunityInvestment(
          buildPayload('PAYMENT_DONE'),
          headers,
        )
        if (!(paymentRes && paymentRes.data && paymentRes.status === 200)) {
          dispatch(
            showBanner({
              text: paymentRes?.data?.message || 'Could not pay from wallet',
              variant: 'error-banner',
            }),
          )
          return
        }
        paid = paymentRes.data
        setInvestmentData(paid)
      }

      // Step 2: place the order.
      const data = await investServices.updateOpportunityInvestment(
        buildPayload('ORDER_PLACED', paid.metadata),
        headers,
      )
      if (data && data.data && data.status === 200) {
        setInvestmentData(data.data)
        navigateToPaymentSuccessScreen()
        captureEvent(EVENTS.investment_application_submitted, { data })
      } else {
        dispatch(
          showBanner({
            text:
              data?.data?.message ||
              'Payment done, but the order could not be placed. Please try again.',
            variant: 'error-banner',
          }),
        )
      }
    } finally {
      setIsTransitioning(false)
    }
  }

  const navigateToPaymentSuccessScreen = () => {
    const opportunityIdForUrl = accessToken ? opportunity?.id?.toString() : opportunityParamId
    navigate(`/payment/${opportunitySlug}/${opportunityIdForUrl}/submitted-successful`)
  }

  useEffect(() => {
    const host = typeof window !== 'undefined' ? window.location.hostname : ''
    if (host === 'localhost' || host === '127.0.0.1') {
      setShowPopupBlockedModal(false)
      return
    }
    const popup = window.open('', 'popup', 'width=1,height=1')
    setShowPopupBlockedModal(!popup)
    popup?.close()
  }, [])

  useEffect(() => {
    if (
      (opportunityParamId !== 'undefined' && opportunitySlug !== 'undefined') ||
      (accessToken && opportunitySlug !== 'undefined')
    ) {
      fetchOpportunityById()
      getUserInvestments()
      if (opportunityParamId && !accessToken) {
        getInvestment()
      }
    } else {
      navigate('/')
    }
  }, [])

  useEffect(() => {
    if (accessToken && opportunity?.id) {
      getInvestment()
    }
  }, [opportunity?.id, accessToken])

  useEffect(() => {
    const getUserInvestmentLatest = async () => {
      const opportunityId = accessToken
        ? opportunity?.id
        : opportunityParamId
        ? parseInt(opportunityParamId, 10)
        : 0
      if (!opportunityId) return

      const response = await investServices.getInvestmentLatest_v1(
        { opportunityId },
        {
          Authorization: authToken ? authToken : '',
        },
      )
      if (response && response.data && response.status === 200) {
        if (
          response.data.status === 'ORDER_PLACED' &&
          !investmentNeedsWalletPayment(response.data.status, response.data.metadata)
        ) {
          navigate(-1)
        }
      }
    }
    const getKYC = async () => {
      const response = await kycServices.getKYC_v1(userId.toString(), {
        Authorization: authToken ? authToken : '',
      })
      if (!!response && response.data && response.status === 200) {
        if (response.data.status !== 'APPROVED') {
          dispatch(
            showBanner({
              text: 'Please complete the KYC for investing in the opportunities.',
              variant: 'error-banner',
            }),
          )
          storeOpportunityComplianceReturn(opportunitySlug, opportunityParamId, accessToken)
          navigate('/profile/kyc')
        }

        if (!userHasEditedRiskAckRef.current) {
          setInvestmentEssentialsAck(!!response.data.investment_essentials_agreement)
        }
        setIsTransactionChecklistChecked(!!response.data.transaction_checklist_agreement)
        if (!userHasEditedTobRef.current && response.data.accept_tob) {
          setAcceptTOBCheckbox(true)
        }
      }
    }
    if (authToken) {
      getKYC()
      getUserInvestmentLatest()
    }
  }, [])

  const setAcceptTOBCheckboxTracked = useCallback((value: SetStateAction<boolean>) => {
    userHasEditedTobRef.current = true
    setAcceptTOBCheckbox(value)
  }, [])

  const prevWizardStepRef = useRef(wizardStep)

  const syncStep2AcknowledgementsFromApi = useCallback(async () => {
    if (!authToken || !userId) return
    userHasEditedRiskAckRef.current = false
    userHasEditedTobRef.current = false

    const kyc = await kycServices.getKYC_v1(userId.toString(), {
      Authorization: authToken,
    })
    if (kyc?.data && kyc.status === 200) {
      setInvestmentEssentialsAck(!!kyc.data.investment_essentials_agreement)
      setAcceptTOBCheckbox(!!kyc.data.accept_tob)
    }

    if (isTermsheetAgreementNeeded && opportunity?.id && investmentData?.id) {
      await syncExistingTermsheetState()
    }
  }, [
    authToken,
    investmentData?.id,
    isTermsheetAgreementNeeded,
    opportunity?.id,
    syncExistingTermsheetState,
    userId,
  ])

  useEffect(() => {
    const prev = prevWizardStepRef.current
    prevWizardStepRef.current = wizardStep
    if (prev === 3 && wizardStep === 2) {
      void syncStep2AcknowledgementsFromApi()
    }
  }, [wizardStep, syncStep2AcknowledgementsFromApi])

  useEffect(() => {
    if (wizardStep >= 2 && isTermsheetAgreementNeeded && investmentData?.id) {
      void syncExistingTermsheetState()
    }
  }, [wizardStep, isTermsheetAgreementNeeded, investmentData?.id, syncExistingTermsheetState])

  if (!opportunity) {
    return null
  }

  const transferPageEnabled = isTransferPageEnabled(opportunity)

  return (
    <>
      <InvestV2Presenter
        wizardStep={wizardStep}
        opportunity={opportunity}
        transferPageEnabled={transferPageEnabled}
        userInvestmentDetail={userInvestmentDetail}
        investmentValue={investmentValue}
        setInvestmentValue={setInvestmentValue}
        interestIncomePreference={interestIncomePreference}
        setInterestIncomePreference={setInterestIncomePreference}
        investmentValueForTermsheet={investmentValueForTermsheet}
        investmentData={investmentData}
        investmentEssentialsAck={investmentEssentialsAck}
        onInvestmentEssentialsChange={(checked) => {
          userHasEditedRiskAckRef.current = true
          if (checked) void handleAcknowledgeInvestmentEssentials()
          else setInvestmentEssentialsAck(false)
        }}
        onSignTermsheet={() => void handleAcceptTermsheet()}
        onReopenTermsheetSigning={handleReopenTermsheetSigning}
        onDownloadSignedTermsheet={handleDownloadSignedTermsheet}
        onTermsheetSigningComplete={handleTermsheetSigningComplete}
        termsheetDownloadLink={termsheetDownloadLink}
        termsheetSigningLink={termsheetSigningLink}
        termsheetRequiresResign={termsheetRequiresResign}
        termsheetResignMessage={termsheetResignMessage}
        isTermsheetSigningPending={isTermsheetSigningPending}
        acceptTOBCheckbox={acceptTOBCheckbox}
        setAcceptTOBCheckbox={setAcceptTOBCheckboxTracked}
        isTransactionChecklistChecked={isTransactionChecklistChecked}
        setIsTransactionChecklistChecked={setIsTransactionChecklistChecked}
        showTransactionChecklistModal={showTransactionChecklistModal}
        setTransactionChecklistModal={setTransactionChecklistModal}
        isTermsheetAgreementChecked={isTermsheetAgreementChecked}
        isTermsheetAgreementNeeded={isTermsheetAgreementNeeded}
        showPopupBlockedModal={showPopupBlockedModal}
        isTransitioning={isTransitioning}
        isInlineAckLoading={isInlineAckLoading || isTermsheetSigningPending}
        isPrimaryDisabled={isPrimaryDisabled}
        sharePricing={sharePricing}
        onShareOrderChange={handleShareOrderChange}
        setIsApiError={setIsApiError}
        backToProjectHandler={handleCancelFromAmountStep}
        onWizardBack={onWizardBack}
        onAmountContinue={handleInvestmentAmount}
        onReviewContinue={handleAgreementandBankDetailsConfirmation}
        onRequestLimitExtension={() => void openIfAllowed(() => setShowExtendLimitModal(true))}
        hasPendingLimitRequest={hasPendingRequest}
        wallet={wallet}
        pendingTopup={pendingTopup}
        onPayFromWallet={handlePayFromWallet}
        onRefreshWallet={refreshWallet}
      />
      {showExtendLimitModal ? (
        <ExtendLimitModal
          onClose={() => setShowExtendLimitModal(false)}
          onSubmitted={markPending}
        />
      ) : null}
    </>
  )
}

export { InvestV2Container }
